import type { FormContext } from "vee-validate"
import type { MaybeRefOrGetter } from "vue"
import type { ISelectItem } from "@/components/ui/Select/select.interface"
import { computed, toValue, watchEffect } from "vue"
import { useListEventTypes } from "@/api/listEventTypes"
import { useTranslation } from "@/composables/useTranslation"
import { eventTypeTranslationKey } from "@/models/eventType"

interface EventTypeFormValues {
  type?: number
}

export interface UseEventTypeSelectOptions {
  /** The event's currently stored type, if any. May be missing from the offered event types. */
  storedTypeId?: MaybeRefOrGetter<number | null | undefined>
}

export interface UseEventTypeSelectDefaultOptions {
  /** When set, force the type field to this value instead of applying the brand default. */
  forcedType?: MaybeRefOrGetter<number | null | undefined>
  /** True when editing an existing event rather than creating a new one. */
  hasExistingRecord?: MaybeRefOrGetter<boolean>
}

/**
 * Shared event-type Select logic for EventDetailsForm and the event creation page.
 * Call `applyDefault` once `useForm`'s `values`/`setFieldValue` exist.
 */
export function useEventTypeSelect(options: UseEventTypeSelectOptions = {}) {
  const { t } = useTranslation()

  const { data: eventTypes, isLoading: isLoadingEventTypes } = useListEventTypes()

  const selectEventTypes = computed<ISelectItem<number>[]>(() => {
    const items = eventTypes.value?.map(type => ({ value: type.id, label: type.translationKey ? t(type.translationKey) : type.title || t("event_details_form.event_types.other") })) ?? []

    const storedTypeId = toValue(options.storedTypeId)
    if (storedTypeId != null && !items.some(item => item.value === storedTypeId)) {
      items.unshift({ value: storedTypeId, label: t(eventTypeTranslationKey(storedTypeId) ?? "event_details_form.event_types.other") })
    }

    return items
  })

  function isValidEventType(id: number | undefined): boolean {
    if (id == null) {
      return false
    }

    if (eventTypes.value?.some(type => type.id === id)) {
      return true
    }

    return id === toValue(options.storedTypeId)
  }

  function applyDefault(
    form: Pick<FormContext<EventTypeFormValues>, "values" | "setFieldValue">,
    defaultOptions: UseEventTypeSelectDefaultOptions = {},
  ) {
    watchEffect(() => {
      const forcedType = toValue(defaultOptions.forcedType)
      if (forcedType != null) {
        form.setFieldValue("type", forcedType)
        return
      }

      if (toValue(defaultOptions.hasExistingRecord) || form.values.type !== undefined) {
        return
      }

      const defaultEventType = eventTypes.value?.find(type => type.isDefault)
      if (defaultEventType) {
        form.setFieldValue("type", defaultEventType.id)
      }
    })
  }

  return {
    eventTypes,
    isLoadingEventTypes,
    selectEventTypes,
    isValidEventType,
    applyDefault,
  }
}
